/**
 * Confirmation card for an agent-proposed WRITE action. Shows the tool, a
 * tier badge, a before→after diff, and Approve/Reject — Approve requires a
 * typed reason (enforced server-side too).
 *
 * `collapsible` (used for terminal/historic actions) renders the card
 * collapsed to a one-line header so executed/rejected cards don't pile up;
 * click the header to expand the diff. Pending cards are always expanded.
 */
import { useState } from 'react';

import { useConfirmAgentAction } from '@/lib/api/clinicalAdminHooks';
import {
  ORDER_ACTION_TOOL_MAP,
  reasonCodeLabel,
  selectableReasonCodes,
} from '@/lib/orderActionReasons';
import type { AgentPendingAction } from '@/types/clinicalAdmin';

import { isReasonValid, ReasonPicker } from './ReasonPicker';
import { Badge, fmtDateTime } from './ui';

function fmtVal(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function tierTone(tier: number): 'neutral' | 'warn' | 'danger' {
  if (tier >= 3) return 'danger';
  if (tier >= 2) return 'warn';
  return 'neutral';
}

function statusTone(
  s: AgentPendingAction['status'],
): 'neutral' | 'good' | 'warn' | 'danger' {
  if (s === 'executed') return 'good';
  if (s === 'failed') return 'danger';
  if (s === 'rejected' || s === 'expired') return 'warn';
  return 'neutral';
}

export function ConfirmActionCard({
  action,
  threadId,
  collapsible = false,
}: {
  action: AgentPendingAction;
  threadId: string;
  collapsible?: boolean;
}) {
  const confirm = useConfirmAgentAction(threadId);
  const [reason, setReason] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [open, setOpen] = useState(false);
  const orderActionType = ORDER_ACTION_TOOL_MAP[action.tool_name];

  const before = action.before_snapshot ?? {};
  const after = action.after_snapshot ?? {};
  const keys = Array.from(
    new Set([...Object.keys(before), ...Object.keys(after)]),
  );
  const pending = action.status === 'pending';
  const showDetail = !collapsible || open;

  return (
    <div
      className={`border border-line rounded bg-raised flex flex-col gap-12 ${
        collapsible && !open ? 'p-10' : 'p-15'
      }`}
    >
      <div
        className={`flex items-center justify-between gap-12 ${
          collapsible ? 'cursor-pointer select-none' : ''
        }`}
        onClick={collapsible ? () => setOpen((v) => !v) : undefined}
      >
        <div className="flex items-center gap-8 min-w-0">
          {collapsible ? (
            <span
              aria-hidden
              className={`text-ink-muted transition-transform ${
                open ? 'rotate-90' : ''
              }`}
            >
              ▸
            </span>
          ) : null}
          <span className="type-label-sm font-mono">{action.tool_name}</span>
          {action.target_ref ? (
            <span className="type-label-sm text-ink-muted truncate">
              {action.target_ref}
            </span>
          ) : null}
          <Badge tone={tierTone(action.tier)}>Tier {action.tier}</Badge>
          {collapsible ? (
            <span className="type-10 text-ink-muted whitespace-nowrap">
              {fmtDateTime(action.created_at)}
            </span>
          ) : null}
        </div>
        <Badge tone={statusTone(action.status)}>{action.status}</Badge>
      </div>

      {showDetail ? (
        <>
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <th className="text-left px-10 py-6 font-normal text-ink-muted">
                    Field
                  </th>
                  <th className="text-left px-10 py-6 font-normal text-ink-muted">
                    Before
                  </th>
                  <th className="text-left px-10 py-6 font-normal text-ink-muted">
                    After
                  </th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => {
                  const b = fmtVal(before[k]);
                  const a = fmtVal(after[k]);
                  const changed = b !== a;
                  return (
                    <tr key={k} className="border-t border-line-subtle">
                      <td className="px-10 py-6 font-mono text-ink-muted">
                        {k}
                      </td>
                      <td className="px-10 py-6 break-words">{b}</td>
                      <td
                        className={`px-10 py-6 break-words ${
                          changed ? 'text-accent font-medium' : ''
                        }`}
                      >
                        {a}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {action.error ? (
            <div className="type-label-sm text-danger">
              Error: {action.error}
            </div>
          ) : null}
          {action.reason || action.reason_code ? (
            <div className="type-label-sm text-ink-muted">
              Reason:{' '}
              {action.reason_code ? reasonCodeLabel(action.reason_code) : null}
              {action.reason_code && action.reason ? ' — ' : null}
              {action.reason}
            </div>
          ) : null}

          {pending ? (
            <div className="flex flex-col gap-8">
              {orderActionType ? (
                <ReasonPicker
                  codes={selectableReasonCodes(orderActionType)}
                  labelFor={reasonCodeLabel}
                  value={{ reasonCode, reason }}
                  onChange={(next) => {
                    setReasonCode(next.reasonCode);
                    setReason(next.reason);
                  }}
                  disabled={confirm.isPending}
                  label="Reason (required to approve)"
                />
              ) : (
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (required to approve)…"
                  rows={2}
                  className="type-label-sm px-10 py-8 rounded border border-line bg-white w-full"
                />
              )}
              <div className="flex items-center gap-8">
                <button
                  type="button"
                  disabled={
                    (orderActionType
                      ? !isReasonValid({ reasonCode, reason })
                      : !reason.trim()) || confirm.isPending
                  }
                  onClick={() =>
                    confirm.mutate({
                      actionId: action.id,
                      decision: 'approve',
                      reason: reason.trim() || undefined,
                      reasonCode: orderActionType ? reasonCode : undefined,
                    })
                  }
                  className="h-30 px-12 bg-ink text-page type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {confirm.isPending ? 'Working…' : 'Approve & run'}
                </button>
                <button
                  type="button"
                  disabled={confirm.isPending}
                  onClick={() =>
                    confirm.mutate({
                      actionId: action.id,
                      decision: 'reject',
                      reason: reason.trim() || undefined,
                      reasonCode: orderActionType ? reasonCode || undefined : undefined,
                    })
                  }
                  className="h-30 px-12 bg-danger-wash text-danger type-label-sm rounded hover:opacity-90 disabled:opacity-40"
                >
                  Reject
                </button>
              </div>
              {confirm.isError ? (
                <div className="type-label-sm text-danger">
                  {confirm.error instanceof Error
                    ? confirm.error.message
                    : 'Could not submit decision.'}
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
